import { useEffect, useRef, useState } from 'react';
import type { MediaItem } from '@cornerstone/shared';
import { api } from '../client/api';
import { useCalibrationV2Store } from '../store/calibrationV2';
import {
  createWarpV2Renderer,
  type MediaTextureSourceV2,
  type WarpV2Renderer,
} from './warpV2Renderer';

// =============================================================================
// Calibration v2 렌더러 hook — v2 store 상태를 구독해 캔버스에 그린다.
//   - 드래그 중에는 마스크를 draft(1/4) 품질로 래스터, 드롭 시 full로 재렌더.
//   - 미디어가 비디오면 rAF 루프로 매 프레임 텍스처를 갱신한다.
// =============================================================================

export function useWarpV2Renderer(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  media: MediaTextureSourceV2 | null,
): void {
  const rendererRef = useRef<WarpV2Renderer | null>(null);

  const surfaces = useCalibrationV2Store((s) => s.surfaces);
  const pattern = useCalibrationV2Store((s) => s.testPattern);
  const heatmap = useCalibrationV2Store((s) => s.heatmapEnabled);
  const bypassWarp = useCalibrationV2Store((s) => s.bypassWarp);
  const isDragging = useCalibrationV2Store((s) => s.isDragging);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    rendererRef.current = createWarpV2Renderer(canvas);
    return () => {
      rendererRef.current?.destroy();
      rendererRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const drawOnce = (): void => {
      rendererRef.current?.draw({
        surfaces,
        pattern,
        heatmap,
        bypassWarp,
        media,
        maskQuality: isDragging ? 'draft' : 'full',
      });
    };

    drawOnce();
    window.addEventListener('resize', drawOnce);

    // 비디오 소스 — 매 프레임 텍스처 갱신 rAF 루프.
    let raf: number | null = null;
    if (media && media.element instanceof HTMLVideoElement) {
      const loop = (): void => {
        drawOnce();
        raf = window.requestAnimationFrame(loop);
      };
      raf = window.requestAnimationFrame(loop);
    }

    return () => {
      window.removeEventListener('resize', drawOnce);
      if (raf !== null) window.cancelAnimationFrame(raf);
    };
  }, [surfaces, pattern, heatmap, bypassWarp, isDragging, media]);
}

// =============================================================================
// useMediaTextureV2 — 미디어 라이브러리 아이템을 캔버스 텍스처 소스로 로드.
// 비디오는 muted + loop 자동 재생 (PLAN §8 보류 항목 — 프리뷰 정책).
// =============================================================================

export function useMediaTextureV2(item: MediaItem | null): MediaTextureSourceV2 | null {
  const [source, setSource] = useState<MediaTextureSourceV2 | null>(null);

  useEffect(() => {
    if (!item) {
      setSource(null);
      return;
    }
    let cancelled = false;

    if (item.mediaType === 'video') {
      const video = document.createElement('video');
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.src = api.mediaFileUrl(item.id);
      const publishReadyFrame = (): void => {
        if (cancelled) return;
        setSource({
          element: video,
          width: video.videoWidth || 1,
          height: video.videoHeight || 1,
          fit: 'contain',
        });
      };
      const onReady = (): void => {
        if (cancelled) return;
        void video.play().catch(() => undefined);
        // loadeddata 직후에는 메타데이터만 준비되고 GPU에 업로드할 첫 프레임이
        // 아직 없는 브라우저가 있다. 실제 프레임 콜백 뒤 texture source를 공개한다.
        if ('requestVideoFrameCallback' in video) {
          video.requestVideoFrameCallback(() => publishReadyFrame());
        } else {
          publishReadyFrame();
        }
      };
      video.addEventListener('loadeddata', onReady);
      video.load();
      return () => {
        cancelled = true;
        video.removeEventListener('loadeddata', onReady);
        video.pause();
        video.removeAttribute('src');
        video.load();
        setSource(null);
      };
    }

    const img = new Image();
    img.src = api.mediaFileUrl(item.id);
    const onLoad = (): void => {
      if (cancelled) return;
      setSource({
        element: img,
        width: img.naturalWidth || 1,
        height: img.naturalHeight || 1,
        fit: 'contain',
      });
    };
    img.addEventListener('load', onLoad);
    return () => {
      cancelled = true;
      img.removeEventListener('load', onLoad);
      setSource(null);
    };
  }, [item]);

  return source;
}
